const HEIC_EXT_RE = /\.(heic|heif)$/i;
const HEIC_MIME = new Set(['image/heic', 'image/heif']);

export const isHeicFile = (file: File): boolean =>
  HEIC_MIME.has(file.type) || HEIC_EXT_RE.test(file.name);

// heic2any frequently returns "valid-looking" JPEG blobs whose payload is
// degenerate — the browser loads them but with naturalWidth/Height = 0, so
// onerror never fires and downstream <img> renders show the alt-text fallback
// (this is what was breaking the profile-pic cropper). The gallery upload
// path hides this by running its own canvas-based compression after the
// conversion, which inadvertently re-encodes the broken JPEG into a real
// one. We do the same re-encode here so both pipelines get trustworthy
// output from this helper directly.
export const convertHeicToJpeg = async (file: File): Promise<File> => {
  const { default: heic2any } = await import('heic2any');
  const result = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.82 });
  const heicJpeg = Array.isArray(result) ? result[0] : result;

  if (!heicJpeg || heicJpeg.size === 0) {
    throw new Error(`heic2any returned an empty blob for ${file.name} (input size ${file.size})`);
  }

  const reEncoded = await reEncodeViaCanvas(heicJpeg);
  const newName = file.name.replace(HEIC_EXT_RE, '') + '.jpeg';
  return new File([reEncoded], newName, { type: 'image/jpeg', lastModified: file.lastModified });
};

const reEncodeViaCanvas = (blob: Blob): Promise<Blob> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth === 0 || img.naturalHeight === 0) {
        URL.revokeObjectURL(url);
        reject(new Error(`Decoded image has zero dimensions (${blob.size} bytes, type "${blob.type}") — heic2any returned degenerate JPEG`));
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('Could not get 2D context for canvas re-encode'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      canvas.toBlob(
        (out) => {
          if (!out) {
            reject(new Error('canvas.toBlob returned null'));
            return;
          }
          resolve(out);
        },
        'image/jpeg',
        0.82,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Source blob is not decodable for canvas re-encode (${blob.size} bytes, type "${blob.type}")`));
    };
    img.src = url;
  });

export const convertHeicFiles = async (
  files: File[]
): Promise<{ files: File[]; failed: { file: File; error: unknown }[] }> => {
  const failed: { file: File; error: unknown }[] = [];
  const out: File[] = [];
  for (const f of files) {
    if (!isHeicFile(f)) {
      out.push(f);
      continue;
    }
    try {
      out.push(await convertHeicToJpeg(f));
    } catch (error) {
      console.error('[heicConvert] Failed to convert HEIC:', { name: f.name, size: f.size, error });
      failed.push({ file: f, error });
    }
  }
  return { files: out, failed };
};
